import type {
  ConversationAnswerResource,
  ConversationResource,
} from "api/generated";
import { mapAttachments, mapLinks, mapVideos } from "shared/mappers";
import type { Conversation, ConversationAnswer } from "conversations/types";
import { conversationQuestionFromDto } from "./conversationQuestion.mapper";
import { parseSchoolDateTime } from "shared/utils";

const conversationAnswerFromDto = (
  dto: ConversationAnswerResource,
): ConversationAnswer => ({
  id: dto.id,
  answer: dto.answer,
  question: conversationQuestionFromDto(dto.question),
});

/**
 * Maps the create and edit responses (`ConversationResource`). `GET api/conversations/{id}`
 * answers with the docent's detail payload instead — see
 * `teacherConversationDetailFromDto`.
 *
 * `date` is one datetime carrying a Dutch wall-clock moment, so it is read through the
 * school clock rather than the browser's timezone — see `parseSchoolDateTime`.
 */
export const conversationFromDto = (
  dto: ConversationResource,
): Conversation => ({
  id: dto.id,
  name: dto.name,
  teacherNotes: dto.teacher_notes ?? "",
  studentNotes: dto.student_notes ?? "",
  subjectShortage: dto.subject_shortage,
  date: parseSchoolDateTime(dto.date),
  endDate: parseSchoolDateTime(dto.end_date),
  isNote: dto.is_note,
  conversationInvitationId: dto.conversation_invitation_id ?? null,
  conversationType: {
    id: dto.conversation_type.id,
    name: dto.conversation_type.name,
  },
  student: {
    id: dto.student.id,
    name: dto.student.name,
  },
  answers: dto.answers.map(conversationAnswerFromDto),
  attachments: mapAttachments(dto.attachments),
  links: mapLinks(dto.links),
  videos: mapVideos(dto.videos),
});
